import { PendingMessagesAlert } from "@/components/common/section-components/alert/PendingMessagesAlert";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useRoles } from "@/hooks/useRoles";
import { Flex, HStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  SlotRequestEntity,
  SlotRequestListResponse,
} from "./schema/slot-requests.schema";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { AppRoutes } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import { SlotRequestActions } from "@/components/common/section-components/action/SlotRequestActions";
import { SlotRequestFilter } from "@/components/common/section-components/filters/SlotRequestFilter";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import {
  clearFiltersHandler,
  fetchSlotRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { NoData } from "@/components/common/section-components/NoData";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SlotRequestStatus } from "@/constants/entity-status";
import { StandardTable } from "@/components/table/StandardTable";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { slotRequestsHeaderElements } from "./schema/header-elements";
import { usePolling } from "@/hooks/usePolling";
import { EstadosCupos } from "@/constants/enums/status/entity-status-ids";

export const SlotRequestsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const { fetchPermissions } = useRoles();

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const navigate = useNavigate();
  const [loadingSlotRequests, setLoadingSlotRequests] =
    useState<boolean>(false);

  const [currentFilters, setCurrentFilters] = useState<any>();

  const [page, setPage] = useState<number>(1);

  const fetchSlotRequests = async (filters?: any, showLoad = true) => {
    await fetchSlotRequestsHandler({
      setLoadingSlotRequests,
      filters,
      showLoad,
      setSlotRequests,
      navigate,
      fetchPermissions,
    });
  };

  const { control, handleSubmit, setValue, getValues } = useForm();
  const [slotRequests, setSlotRequests] = useState<SlotRequestListResponse>();

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    submitFiltersHandler({
      event,
      fetchSlotRequests,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
    });
  };

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setOpenFilters,
      setValue,
      submitFilters,
    });
  };

  useEffect(() => {
    if (isActive) fetchSlotRequests();
  }, [isActive]);

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchSlotRequests({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (item: SlotRequestEntity) => {
    navigate(AppRoutes.SLOT_REQUEST_DETAIL.replace(":id", String(item?.id)));
  };
  const getActionElements = (item: SlotRequestEntity) => (
    <SlotRequestActions handleDetail={handleDetail} item={item} />
  );

  const values = useWatch({
    control,
  });

  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  usePolling({
    callback: () => fetchSlotRequests(currentFilters, false),
    delay: 15000,
    isActive
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader title="Solicitud de Cupos" loading={loadingSlotRequests} />

      <HStack>
        {slotRequests?.pendingRequests && !loadingSlotRequests && (
          <PendingMessagesAlert
            submitFilters={submitFilters}
            setValue={setValue}
            statusToSet={[EstadosCupos.RequiereInformacion]}
          />
        )}

        <SlotRequestFilter
          clearFilters={clearFilters}
          control={control}
          handleSubmit={handleSubmit}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          submitFilters={submitFilters}
          filtersApplied={filtersApplied}
          disableApply={filtersEmpty}
          maxDate={dayjs()}
          disableFilters={loadingSlotRequests}
        />
      </HStack>

      <Flex flexDir="column">
        {Boolean(!loadingSlotRequests && !slotRequests?.listado?.length) && (
          <NoData />
        )}
        {Boolean(!loadingSlotRequests && slotRequests?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                slotRequests?.listado?.map((el: SlotRequestEntity) => ({
                  ...el,
                  fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),

                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as SlotRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={slotRequestsHeaderElements}
              actionElements={getActionElements}
            />
            {Number(slotRequests?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={slotRequests?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
